/**
 * BidSourcingPanel
 * ----------------
 * Subcontractor selection matrix → RFQ dispatch → bid comparison → award →
 * elevate the awarded trades into an active project.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Star, ShieldCheck, AlertTriangle, Trophy, ArrowUpRight,
  Search, Mail, FileText, Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";

const fmt$ = (n) => n == null ? "—" :
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INV_PILL = {
  sent: "bg-slate-100 text-slate-600 ring-slate-300",
  opened: "bg-blue-50 text-blue-700 ring-blue-300",
  bid_received: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  declined: "bg-red-50 text-red-700 ring-red-300",
};

export default function BidSourcingPanel({ sol, token, onConverted }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const trades = (sol?.parsed?.trades || []).map((t) => t.label).filter(Boolean);
  const [trade, setTrade] = useState(trades[0] || "");
  const [filters, setFilters] = useState({ cert: "", min_rating: "", compliant_only: false, q: "" });
  const [dir, setDir] = useState({ contractors: [], cert_types: [] });
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [note, setNote] = useState("");
  const [invites, setInvites] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [rating, setRating] = useState(null);

  const loadDir = useCallback(async () => {
    if (!trade) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ trade });
      if (filters.cert) q.set("cert", filters.cert);
      if (filters.min_rating) q.set("min_rating", filters.min_rating);
      if (filters.compliant_only) q.set("compliant_only", "true");
      const r = await axios.get(`${API}/bid-desk/contractors?${q}`, { headers });
      setDir(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the contractor directory");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade, filters.cert, filters.min_rating, filters.compliant_only, token]);
  useEffect(() => { loadDir(); }, [loadDir]);

  const loadInvites = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/solicitations/${sol.id}/invitations`, { headers });
      setInvites(r.data?.invitations || []);
      setComparison(r.data?.comparison || []);
    } catch { /* first load on a fresh solicitation */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sol.id, token]);
  useEffect(() => { loadInvites(); }, [loadInvites]);

  const rows = (dir.contractors || []).filter((c) =>
    !filters.q || `${c.company_name} ${c.email} ${c.trades.join(" ")}`.toLowerCase()
      .includes(filters.q.toLowerCase()));

  const dispatch = async () => {
    if (!picked.length) return toast.error("Tick at least one subcontractor");
    setDispatching(true);
    try {
      const chosen = rows.filter((c) => picked.includes(c.key));
      const r = await axios.post(`${API}/bid-desk/solicitations/${sol.id}/dispatch`, {
        trade, contractors: chosen, message: note,
        portal_base: process.env.REACT_APP_BACKEND_URL,
      }, { headers });
      const sent = r.data?.sent || [];
      const failed = r.data?.failed || [];
      if (sent.length) {
        toast.success(`RFQ sent to ${sent.length} ${trade} sub${sent.length === 1 ? "" : "s"}`, {
          description: "Branded RFQ PDF attached, with a link for them to quote straight back",
        });
      }
      if (failed.length) toast.error(`${failed.length} failed: ${failed[0].error}`);
      setPicked([]); setNote(""); loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally { setDispatching(false); }
  };

  const saveRating = async () => {
    try {
      await axios.put(`${API}/bid-desk/contractors/${rating.source}/${rating.id}`, {
        performance_rating: Number(rating.performance_rating || 0),
        certifications: (rating.certifications || []).filter((c) => c.type),
      }, { headers });
      toast.success(`${rating.company_name} updated`);
      setRating(null); loadDir();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const award = async (inv, awarded = true) => {
    try {
      await axios.post(`${API}/bid-desk/invitations/${inv.id}/award`, { awarded }, { headers });
      toast.success(awarded ? `${inv.company_name} awarded ${inv.trade}` : "Award cleared");
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not award");
    }
  };

  const convert = async () => {
    if (!window.confirm("Elevate the awarded trades into an active project?")) return;
    try {
      const r = await axios.post(`${API}/bid-desk/solicitations/${sol.id}/convert-to-project`, {}, { headers });
      toast.success("Project created", {
        description: `Contract value ${fmt$(r.data.contract_value)} · committed sub cost ${fmt$(r.data.committed_cost)}`,
      });
      onConverted?.(r.data.project_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert");
    }
  };

  const awardedCount = invites.filter((i) => i.awarded).length;

  return (
    <div className="space-y-5" data-testid="bid-sourcing-panel">
      {/* selection matrix */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Subcontractor selection matrix</p>
            <p className="text-sm text-slate-500">
              Pick the trade, filter the bench, tick who gets the RFQ.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={trade} onChange={(e) => { setTrade(e.target.value); setPicked([]); }}
              className="border border-slate-300 rounded-md h-9 px-2 text-sm" data-testid="matrix-trade-select">
              {trades.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filters.cert} onChange={(e) => setFilters({ ...filters, cert: e.target.value })}
              className="border border-slate-300 rounded-md h-9 px-2 text-sm" data-testid="matrix-cert-filter">
              <option value="">Any certification</option>
              {(dir.cert_types || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.min_rating} onChange={(e) => setFilters({ ...filters, min_rating: e.target.value })}
              className="border border-slate-300 rounded-md h-9 px-2 text-sm" data-testid="matrix-rating-filter">
              <option value="">Any rating</option>
              {[3, 3.5, 4, 4.5].map((r) => <option key={r} value={r}>{r}+ stars</option>)}
            </select>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={filters.compliant_only}
                onChange={(e) => setFilters({ ...filters, compliant_only: e.target.checked })}
                data-testid="matrix-compliant-filter" />
              COI &amp; licence current
            </label>
          </div>
        </div>

        <div className="relative mb-2">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input className="pl-8" placeholder="Search company, email or trade" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="matrix-search" />
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 inline-flex items-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin" />Loading bench…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">
            No {trade} subs match. Loosen the filters, or add the contractor in the Contractors module first.
          </p>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full min-w-[820px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {["", "Company", "Trades", "Certifications", "Rating", "Compliance"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.key} className={picked.includes(c.key) ? "bg-blue-50/50" : ""}
                    data-testid={`matrix-row-${c.id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={picked.includes(c.key)}
                        onChange={(e) => setPicked(p => e.target.checked ? [...p, c.key] : p.filter(k => k !== c.key))}
                        data-testid={`matrix-check-${c.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-sm font-semibold text-slate-900">{c.company_name}</p>
                      <p className="text-[11px] text-slate-500">{c.email}{c.city ? ` · ${c.city}` : ""}</p>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">{c.trades.slice(0, 3).join(", ") || "—"}</td>
                    <td className="px-3 py-2">
                      {(c.cert_status.active || []).map((t) => (
                        <span key={t} className="inline-block mr-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-1.5">{t}</span>
                      ))}
                      {(c.cert_status.expired || []).map((t) => (
                        <span key={t} className="inline-block mr-1 text-[10px] font-bold text-red-600 bg-red-50 ring-1 ring-red-200 rounded-full px-1.5">{t} exp</span>
                      ))}
                      {!(c.cert_status.active || []).length && !(c.cert_status.expired || []).length && (
                        <span className="text-[11px] text-slate-400">none</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => setRating({ ...c })}
                        className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-blue-700"
                        data-testid={`matrix-rate-${c.id}`}>
                        <Star className={`w-3.5 h-3.5 ${c.performance_rating ? "text-amber-500" : "text-slate-300"}`} />
                        {c.performance_rating ?? "rate"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {c.compliant
                        ? <span className="text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />current</span>
                        : <span className="text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />check COI / licence</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 grid sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Add a line to the cover email (optional)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="We are targeting a walk-through Thursday morning — reply if you need access."
              data-testid="dispatch-note" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPicked(rows.map(r => r.key))}
              data-testid="matrix-select-all">Select all</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={dispatch}
              disabled={dispatching || !picked.length} data-testid="dispatch-btn">
              {dispatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send RFQ to {picked.length || 0}
            </Button>
          </div>
        </div>
      </div>

      {/* comparison + invitations */}
      {invites.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Bids by trade</p>
              <p className="text-sm text-slate-500">Low bid, spread and who is still sitting on it.</p>
            </div>
            {awardedCount > 0 && !sol.converted_project_id && (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={convert}
                data-testid="convert-to-project-btn">
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Convert {awardedCount} awarded trade{awardedCount === 1 ? "" : "s"} to a project
              </Button>
            )}
            {sol.converted_project_id && (
              <a href={`/admin/projects/${sol.converted_project_id}`}
                className="text-sm font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1">
                <Trophy className="w-4 h-4" />Open the project
              </a>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {comparison.map((c) => (
              <div key={c.trade} className="rounded-lg border border-slate-200 p-3" data-testid={`compare-${c.trade}`}>
                <p className="text-sm font-bold text-slate-900">{c.trade}</p>
                <p className="text-[11px] text-slate-500">
                  {c.bids_in} of {c.invited} quoted{c.declined ? ` · ${c.declined} declined` : ""}
                </p>
                <p className="text-lg font-black text-slate-900 mt-1">{fmt$(c.low)}</p>
                {c.spread ? <p className="text-[11px] text-slate-500">spread {fmt$(c.spread)} to {fmt$(c.high)}</p> : null}
                {c.awarded_to ? (
                  <p className="text-[11px] font-bold text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <Award className="w-3 h-3" />{c.awarded_to} · {fmt$(c.awarded_amount)}
                  </p>
                ) : null}
              </div>
            ))}
          </div>

          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50">
                <tr>
                  {["Trade", "Subcontractor", "Status", "Bid", "Duration", "Exclusions", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invites.map((i) => (
                  <tr key={i.id} className={i.awarded ? "bg-emerald-50/50" : ""} data-testid={`invite-row-${i.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-600">{i.trade}</td>
                    <td className="px-3 py-2">
                      <p className="text-sm font-semibold text-slate-900">{i.company_name}</p>
                      <p className="text-[11px] text-slate-500">{i.email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${INV_PILL[i.status]}`}>
                        {(i.status || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm font-bold text-slate-900">{fmt$(i.bid?.amount)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {i.bid?.duration_days ? `${i.bid.duration_days} days` : "—"}
                      {i.bid?.bonded ? <span className="block text-[10px] text-emerald-700">bondable</span> : null}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 max-w-[240px] truncate">
                      {i.bid?.exclusions || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(i.bid?.attachments || []).length > 0 && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${i.bid.attachments[0].url}`}
                          target="_blank" rel="noreferrer" className="inline-block p-1.5 text-slate-500 hover:text-slate-800"
                          title="Their quote file">
                          <FileText className="w-4 h-4" />
                        </a>
                      )}
                      {i.bid && !i.awarded && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => award(i, true)} data-testid={`award-btn-${i.id}`}>
                          <Trophy className="w-3.5 h-3.5 mr-1" />Award
                        </Button>
                      )}
                      {i.awarded && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:bg-slate-100"
                          onClick={() => award(i, false)}>Clear award</Button>
                      )}
                      <a href={`mailto:${i.email}`} className="inline-block p-1.5 text-slate-400 hover:text-slate-700" title="Chase them">
                        <Mail className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* rating / cert editor */}
      <Dialog open={!!rating} onOpenChange={(o) => !o && setRating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rating?.company_name}</DialogTitle>
            <DialogDescription>Performance score and certifications — both drive the matrix filters.</DialogDescription>
          </DialogHeader>
          {rating && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Performance rating (0–5)</label>
                <Input type="number" step="0.5" min="0" max="5" value={rating.performance_rating ?? ""}
                  onChange={(e) => setRating({ ...rating, performance_rating: e.target.value })}
                  data-testid="rating-input" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Certifications</label>
                {(rating.certifications || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2">
                    <Input placeholder="MBE" value={c.type || ""}
                      onChange={(e) => setRating({
                        ...rating,
                        certifications: rating.certifications.map((x, j) => j === i ? { ...x, type: e.target.value } : x),
                      })} data-testid={`cert-type-${i}`} />
                    <Input placeholder="Cert number" value={c.number || ""}
                      onChange={(e) => setRating({
                        ...rating,
                        certifications: rating.certifications.map((x, j) => j === i ? { ...x, number: e.target.value } : x),
                      })} />
                    <Input type="date" value={(c.expiry || "").slice(0, 10)}
                      onChange={(e) => setRating({
                        ...rating,
                        certifications: rating.certifications.map((x, j) => j === i ? { ...x, expiry: e.target.value } : x),
                      })} />
                  </div>
                ))}
                <Button size="sm" variant="outline" data-testid="add-cert-btn"
                  onClick={() => setRating({
                    ...rating,
                    certifications: [...(rating.certifications || []), { type: "", number: "", expiry: "" }],
                  })}>Add certification</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRating(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={saveRating}
              data-testid="save-rating-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
